import API from "./api";
import {
  PULSE_ACCESS_API_ENDPOINT,
  PULSE_BOOKS_API_ENDPOINT,
  PULSE_BOOKS_ERROR_MESSAGE,
  PULSE_DEADLINES_API_ENDPOINT,
  PULSE_FINANCIALS_API_ENDPOINT,
  PULSE_FINANCIALS_DOCUMENTS_API_ENDPOINT,
  PULSE_FINANCIALS_ERROR_MESSAGE,
  PULSE_MONITORING_BANK_ACCOUNTS_API_ENDPOINT,
  PULSE_MONITORING_BANK_ACCOUNTS_ERROR_MESSAGE,
  PULSE_OVERVIEW_API_ENDPOINT,
  PULSE_OVERVIEW_ERROR_MESSAGE,
} from "../constants/pulse.constants";
import {
  PulseApiResponse,
  PulseBooksApiResponse,
  PulseBooksData,
  PulseDeadlineStatus,
  PulseFinancialsData,
  PulseFinancialsUploadResponse,
  PulseMonitoringBankAccountsApiResponse,
  PulseMonitoringBankAccountsData,
  PulseOverviewApiResponse,
  PulseOverviewData,
} from "../interfaces/pulse.interfaces";

class PulseRestService {
  // Pulse company + PULSE lending link for this user. Any failure reads as no
  // access, so the sidebar entry stays hidden and the route guard redirects.
  checkAccess = async (): Promise<boolean> => {
    try {
      const response = await API.get<{ success: boolean; isAccess?: boolean }>(
        PULSE_ACCESS_API_ENDPOINT
      );
      return !!response.data?.isAccess;
    } catch {
      return false;
    }
  };

  getBooks = async (): Promise<PulseBooksData> => {
    try {
      const response = await API.get<PulseBooksApiResponse>(
        PULSE_BOOKS_API_ENDPOINT
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || PULSE_BOOKS_ERROR_MESSAGE);
      }

      return response.data.data;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        PULSE_BOOKS_ERROR_MESSAGE;
      throw new Error(message);
    }
  };

  getOverview = async (): Promise<PulseOverviewData> => {
    try {
      const response = await API.get<PulseOverviewApiResponse>(
        PULSE_OVERVIEW_API_ENDPOINT
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || PULSE_OVERVIEW_ERROR_MESSAGE);
      }

      return response.data.data;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        PULSE_OVERVIEW_ERROR_MESSAGE;
      throw new Error(message);
    }
  };

  getMonitoringBankAccounts =
    async (): Promise<PulseMonitoringBankAccountsData> => {
      try {
        const response = await API.get<PulseMonitoringBankAccountsApiResponse>(
          PULSE_MONITORING_BANK_ACCOUNTS_API_ENDPOINT
        );

        if (!response.data.success || !response.data.data) {
          throw new Error(
            response.data.message || PULSE_MONITORING_BANK_ACCOUNTS_ERROR_MESSAGE
          );
        }

        return response.data.data;
      } catch (error: any) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          PULSE_MONITORING_BANK_ACCOUNTS_ERROR_MESSAGE;
        throw new Error(message);
      }
    };

  getFinancials = (): Promise<PulseFinancialsData> =>
    this.unwrap(
      API.get<PulseApiResponse<PulseFinancialsData>>(
        PULSE_FINANCIALS_API_ENDPOINT
      )
    ).then((response) => response.data as PulseFinancialsData);

  // passwordRequired applies to every file in the request: a locked PDF is
  // stored as-is and its password supplied later via applyFinancialsDocumentPassword.
  uploadFinancialsDocuments = (
    scheduleId: number,
    files: File[],
    passwordRequired = false
  ): Promise<PulseFinancialsUploadResponse> => {
    const formData = new FormData();
    formData.append("scheduleId", String(scheduleId));
    if (passwordRequired) formData.append("passwordRequired", "true");
    files.forEach((file) => formData.append("files", file));
    return this.unwrap(
      API.post<PulseFinancialsUploadResponse>(
        PULSE_FINANCIALS_DOCUMENTS_API_ENDPOINT,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
    );
  };

  applyFinancialsDocumentPassword = (
    docId: number,
    password: string
  ): Promise<PulseApiResponse<never>> =>
    this.unwrap(
      API.post<PulseApiResponse<never>>(
        PULSE_FINANCIALS_DOCUMENTS_API_ENDPOINT,
        { docId, password }
      )
    );

  deleteFinancialsDocuments = (
    docIds: number[]
  ): Promise<PulseApiResponse<never>> =>
    this.unwrap(
      API.post<PulseApiResponse<never>>(
        `${PULSE_FINANCIALS_DOCUMENTS_API_ENDPOINT}/delete`,
        { docIds }
      )
    );

  createDeadlinePromise = (
    scheduleId: number,
    promisedBy: string
  ): Promise<PulseDeadlineStatus> =>
    this.unwrap(
      API.post<PulseApiResponse<PulseDeadlineStatus>>(
        `${PULSE_DEADLINES_API_ENDPOINT}/${scheduleId}/promise`,
        { promisedBy }
      )
    ).then((response) => response.data as PulseDeadlineStatus);

  // Pulse endpoints answer { success, data?, message? }; a success:false body
  // and a thrown HTTP error both surface here as one Error with the message.
  private unwrap = async <T extends { success: boolean; message?: string }>(
    request: Promise<{ data: T }>
  ): Promise<T> => {
    try {
      const response = await request;
      if (!response.data.success) throw new Error(response.data.message);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message ||
          error?.message ||
          PULSE_FINANCIALS_ERROR_MESSAGE
      );
    }
  };
}

export const PulseRestS = new PulseRestService();
